import { Button, ButtonProps, Default, Playback } from '@amzn/dvui/dist/Button'
import { Icon, IconType } from '@amzn/dvui/dist/Icon'
import * as React from 'react'
import * as st from './PlayButton.module.scss'
import * as cn from 'classnames'
import { camelToDataAttrs } from 'DVWebNodeUtils'
import { AtfTreatmentProps } from '../../reducers/features'
import { isTreatment, omit } from '../../utils/utils'

const DeeplinkingClass = 'js-deeplinkable'

const deeplinkDataBlackList = [
    'data-ref',
    'data-fallback-url',
    'data-resume-time',
    'data-title-id',
    'data-is-avod',
    'data-token',
    'data-pageTitle-id',
]

export interface DeeplinkProps {
    fallbackUrl?: string
    titleId?: string
    signInUrl?: string
    sessionId?: string
    pageTitleId?: string
    ref?: string
    videoType?: string
}

const processDeeplinkData = (props: { deepLinkingData: DeeplinkProps }) => {
    const { deepLinkingData = {}, ...otherProps } = props
    const dataAttrs = camelToDataAttrs(deepLinkingData)
    const filteredOtherProps = omit(otherProps, deeplinkDataBlackList)
    return { ...filteredOtherProps, ...dataAttrs }
}


export interface PlayButtonProps extends ButtonProps, AtfTreatmentProps {
    isSeeThrough?: boolean
    deepLinkingData: DeeplinkProps
    href?: string
    isPackshotButton?: boolean
    ariaLabel?: string
}

export const PlayButton: React.SFC<PlayButtonProps> = ({ isSeeThrough = false, isPackshotButton = false, children, ariaLabel, onClick, atfTreatment, ...otherProps }) => {
    const processedProps = processDeeplinkData(otherProps)

    const playButtonClass = cn(
        'dvui-playButton',
        st.circle,
        isSeeThrough ? st.seeThrough : st.opaque,
        { [DeeplinkingClass]: !onClick },
        isPackshotButton ? st.packshotButton : '',
        processedProps.className,
    )

    return (
        <a {...processedProps} onClick={onClick} data-ux-click aria-label={ariaLabel} className={isTreatment(atfTreatment, 'T1', 'T2', 'T3') ? st.clickArea : playButtonClass}>
            {!isTreatment(atfTreatment, 'T1', 'T2', 'T3') &&
            <>
                <Icon use={IconType.PLAY_BUTTON_ARROW} className={st.icon} />
                {children &&
                    <span className="avu-screen-reader-only">{children}</span>
                }
            </>
            }
        </a>
    )
}

export interface PlayBoxProps extends ButtonProps {
    progress?: number
    isLarge?: boolean
    isStartOver?: boolean
    isHover?: boolean
    ariaLabel?: string
    deepLinkingData: DeeplinkProps
    href: string
}

export const PlayBox: React.SFC<PlayBoxProps> = ({ progress = 0, isLarge = false, isStartOver = false, isHover = false, ariaLabel, onClick, ...otherProps }) => {
    const processedProps = processDeeplinkData(otherProps)
    const playButtonClass = cn(
      st.playbox,
      { [st.largePlaybox]: isLarge === true },
      isLarge ? st.largePlaybox : st.smallPlaybox,
      { [DeeplinkingClass]: !onClick },
      processedProps.className,
    )

    const playButtonRightStyling = cn(
      st.playButtonRightContent,
      { [st.hoverOverride]: isHover === true },
    )

    return (
        <a {...processedProps} onClick={onClick} data-ux-click aria-label={ariaLabel} className={playButtonClass}>
            <span className={`${st.circle} ${st.opaque}`}>
                <Icon use={isStartOver ? IconType.START_OVER : IconType.PLAY_BUTTON_ARROW}
                    className={cn(st.icon, { [st.startOverIcon]: isStartOver })}
                />
            </span>

            <span className={playButtonRightStyling}>
                {processedProps.children}
                <ProgressBar progress={progress} />
            </span>
        </a>
    )
}
PlayBox.displayName = 'PlayButton'

const ProgressBar: React.SFC<{ progress?: number }> = ({ progress = 0 }) =>
    progress > 0 ? (
        <span className={st.progress}>
            <span
                aria-valuemax={100}
                aria-valuemin={0}
                aria-valuenow={progress}
                className={st.progressBar}
                role="progressbar"
                style={{ width: `${progress}%` }}
            />
        </span>
    ) : null

export interface SquarePlayButtonProps extends ButtonProps, AtfTreatmentProps {
    progress?: number
    deepLinkingData: DeeplinkProps
    isStartOver?: boolean
}

export const SquarePlayButton: React.SFC<SquarePlayButtonProps> = ({ progress = 0, isStartOver, atfTreatment, ...otherProps }) => {
    const processedProps = processDeeplinkData(otherProps)

    const playButtonClass = cn(
        st.squareButton,
        { [DeeplinkingClass]: !otherProps.onClick },
        otherProps.className,
        processedProps.className
    )

    return (
        <Button
            {...processedProps}
            data-ux-click
            use={isStartOver ? Default : Playback}
            useIcon={isStartOver ? IconType.START_OVER : IconType.PLAY_ARROW}
            className={playButtonClass}
        >
            <span {...(isTreatment(atfTreatment, 'T1', 'T2', 'T3') && { className: st.playButtonText })}>{processedProps.children}</span>
            <ProgressBar progress={progress} />
        </Button>
    )
}
SquarePlayButton.displayName = 'SquarePlayButton'

export interface TrailerButtonProps extends ButtonProps {
    forceDoubleHeight?: boolean
    deepLinkingData: DeeplinkProps;
    href: string
}


export const TrailerButton: React.SFC<TrailerButtonProps> = (props) => {
    const processedProps = processDeeplinkData(props)
    return (
        <Button
            {...processedProps}
            data-ux-click
            useIcon={IconType.TRAILER}
            className={cn({[DeeplinkingClass] : !props.onClick}, props.className)}
        />
    )
}
